import React, { useState, useRef, useEffect } from 'react';

const TOOL_GROUPS = [
  {
    label: 'Sermon Preparation',
    tools: [
      { id: 'sermon',      icon: '📖', name: 'Sermon Prep' },
      { id: 'biblestudy',  icon: '📚', name: 'Bible Study' },
      { id: 'series',      icon: '🗓️', name: 'Series Planner' },
      { id: 'devotional',  icon: '✍️', name: 'Devotional Writer' },
    ],
  },
  {
    label: 'Communication & Care',
    tools: [
      { id: 'announcement', icon: '📢', name: 'Announcement Writer' },
      { id: 'discussion',   icon: '👥', name: 'Discussion Builder' },
      { id: 'pastoral',     icon: '🤝', name: 'Pastoral Care' },
      { id: 'prayer',       icon: '🙏', name: 'Prayer Response' },
      { id: 'evangelism',   icon: '💬', name: 'Evangelism Guide' },
      { id: 'eventpromo',   icon: '🎉', name: 'Event Promo' },
    ],
  },
];

const GOLD = '#C8921A';

export default function Nav({ onNavigate, onLogout, userRole, currentPage }) {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
        setMobileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (name) =>
    setOpenDropdown((prev) => (prev === name ? null : name));

  const navigate = (page) => {
    onNavigate(page);
    setOpenDropdown(null);
    setMobileOpen(false);
  };

  const isActive = (page) => currentPage === page;

  const desktopBtnClass = (active) =>
    `flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      active
        ? 'text-amber-400'
        : 'text-white hover:bg-white hover:bg-opacity-10'
    }`;

  const dropdownItemClass =
    'w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 transition-colors flex items-center gap-2';

  return (
    <nav
      ref={navRef}
      style={{ backgroundColor: '#1B3A5C' }}
      className="relative z-50 shadow-sm"
    >
      <div className="px-6 py-3 flex items-center justify-between">
        {/* Logo */}
        <button
          onClick={() => navigate('dashboard')}
          className="text-white font-bold text-xl hover:opacity-80 transition-opacity flex-shrink-0"
        >
          Ministry AI Toolkit
        </button>

        {/* Desktop nav items */}
        <div className="hidden md:flex items-center gap-1">

          {/* Tools dropdown */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown('tools')}
              className={desktopBtnClass(openDropdown === 'tools')}
            >
              Tools
              <span className="text-xs opacity-60 ml-0.5">▾</span>
            </button>
            {openDropdown === 'tools' && (
              <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                {TOOL_GROUPS.map((group) => (
                  <div key={group.label}>
                    <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                      {group.label}
                    </p>
                    {group.tools.map((tool) => (
                      <button
                        key={tool.id}
                        onClick={() => navigate(tool.id)}
                        className={dropdownItemClass}
                        style={isActive(tool.id) ? { color: GOLD, fontWeight: 600 } : {}}
                      >
                        <span>{tool.icon}</span>
                        <span>{tool.name}</span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* History */}
          <button
            onClick={() => navigate('history')}
            className={desktopBtnClass(isActive('history'))}
          >
            🕐 History
          </button>

          {/* Profile dropdown */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown('profile')}
              className={desktopBtnClass(
                openDropdown === 'profile' || isActive('profile')
              )}
            >
              👤 Profile
              <span className="text-xs opacity-60 ml-0.5">▾</span>
            </button>
            {openDropdown === 'profile' && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                <button
                  onClick={() => navigate('profile')}
                  className={dropdownItemClass}
                >
                  My Profile
                </button>
                <button
                  onClick={() => navigate('profile')}
                  className={dropdownItemClass}
                >
                  Change Password
                </button>
                <hr className="my-1 border-gray-100" />
                <button
                  onClick={() => { setOpenDropdown(null); onLogout(); }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>

          {/* Admin dropdown (admin only) */}
          {userRole === 'admin' && (
            <div className="relative">
              <button
                onClick={() => toggleDropdown('admin')}
                className={desktopBtnClass(
                  openDropdown === 'admin' || isActive('admin')
                )}
              >
                🛡️ Admin
                <span className="text-xs opacity-60 ml-0.5">▾</span>
              </button>
              {openDropdown === 'admin' && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                  <button
                    onClick={() => navigate('admin')}
                    className={dropdownItemClass}
                  >
                    Admin Dashboard
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen((o) => !o)}
          className="md:hidden text-white w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white hover:bg-opacity-10 transition-colors text-xl"
          aria-label="Toggle menu"
        >
          {mobileOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile full-screen overlay menu */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 overflow-y-auto"
          style={{ top: '52px', backgroundColor: '#1B3A5C' }}
        >
          <div className="p-4 space-y-1">
            {TOOL_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="px-3 pt-4 pb-1.5 text-xs font-semibold uppercase tracking-wider text-blue-300">
                  {group.label}
                </p>
                {group.tools.map((tool) => (
                  <button
                    key={tool.id}
                    onClick={() => navigate(tool.id)}
                    className="w-full text-left px-3 py-3 rounded-lg flex items-center gap-3 text-sm transition-colors"
                    style={
                      isActive(tool.id)
                        ? { color: GOLD, backgroundColor: 'rgba(255,255,255,0.08)' }
                        : { color: 'white' }
                    }
                  >
                    <span className="text-lg">{tool.icon}</span>
                    {tool.name}
                  </button>
                ))}
              </div>
            ))}

            <div className="pt-4 border-t border-white border-opacity-10 space-y-1">
              <button
                onClick={() => navigate('history')}
                className="w-full text-left px-3 py-3 rounded-lg text-sm flex items-center gap-3 transition-colors"
                style={isActive('history') ? { color: GOLD } : { color: 'white' }}
              >
                🕐 History
              </button>
              <button
                onClick={() => navigate('profile')}
                className="w-full text-left px-3 py-3 rounded-lg text-sm flex items-center gap-3 transition-colors"
                style={isActive('profile') ? { color: GOLD } : { color: 'white' }}
              >
                👤 My Profile
              </button>
              {userRole === 'admin' && (
                <button
                  onClick={() => navigate('admin')}
                  className="w-full text-left px-3 py-3 rounded-lg text-white text-sm flex items-center gap-3 hover:bg-white hover:bg-opacity-10 transition-colors"
                  style={isActive('admin') ? { color: GOLD } : { color: 'white' }}
                >
                  🛡️ Admin Dashboard
                </button>
              )}
            </div>

            <div className="pt-3 border-t border-white border-opacity-10">
              <button
                onClick={() => { setMobileOpen(false); onLogout(); }}
                className="w-full text-left px-3 py-3 rounded-lg text-red-400 text-sm flex items-center gap-3 hover:bg-white hover:bg-opacity-10 transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
